import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { getCancelEditingPromptModalEvent } from '@ui-common/components/Modal/CancelEditingModal'
import { UseWizardFormReturn } from '@ui-common/hooks/useWizardForm'
import { GlobalServicesContext } from '@ui-common/state/GlobalServiceProvider'
import { isDirtyFromUserInteraction } from '@ui-common/utils/formUtils'
import { FormSectionContainer } from '@ui-components/form/FormSectionContainer'
import { LogoIconType, ProgressBarStepProps } from '@ui-components/global/ComponentProps'
import { IntroSectionProps } from '@ui-components/layout/IntroSection'
import { WizardStep } from '@ui-components/layout/WizardStep'
import { WizardStepProps } from '@ui-components/layout/WizardTypes'
import { PropsWithChildren, use } from 'react'
import { FieldValues, FormProvider, useFormState } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { AnalyticsService, CommonAnalyticsModalName } from '../../analytics'
import { BackToSummaryTopBar } from '../../components/BackToStatusTopBar/BackToSummaryTopBar'
import { getConfirmDependantApplicationDataChangeModal } from '../../components/Modal/ConfirmDependantApplicationDataChangeModal'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useSendPageViewEvent } from '../../hooks/useSendPageViewEvent'
import { getProgressText } from '../../utils/helpers'
import { BusyMode, createStepConfigurations, WizardStepConfig } from '../../utils/wizardUtils'

export interface StepProps<TFormData extends FieldValues> extends Omit<
  WizardStepProps,
  'subHeaderOptions' | 'headerButtonProps' | 'confirmButtonProps' | 'additionalText'
> {
  formMethods: UseWizardFormReturn<TFormData>
  onSubmit: () => void
  submitLabelOverride?: string
  isEditing?: boolean
  onBack?: () => void
  onExit: () => void
  onInfo?: () => void
  analytics: AnalyticsService<string, string, CommonAnalyticsModalName>
  pageNameForAnalytics: string
  customBackAction?: (normalBackAction: (skipDirtyCheck?: boolean) => void) => void
  progressBarOptions: Omit<ProgressBarStepProps, 'aria-label'> | null
  stepId: string
  pageIntro: IntroSectionProps
  /**
   * Set true if saving while editing should confirm clearing other dependant data
   */
  hasDataDependencies: boolean
  onBackToSummary?: () => void
}

export const FormStep = <TFormData extends FieldValues>({
  formMethods,
  onSubmit,
  isLoading = false,
  children,
  customFooter,
  submitLabelOverride,
  stepId,
  isEditing,
  onBack,
  onExit,
  analytics,
  pageNameForAnalytics,
  customBackAction,
  title,
  progressBarOptions,
  pageIntro,
  showExitConfirmation = true,
  shouldShowMainHeader,
  hasDataDependencies,
  onBackToSummary,
  onInfo,
}: PropsWithChildren<StepProps<TFormData>>) => {
  const intl = useIntl()

  const { modalService } = use(GlobalServicesContext)
  const modalSend = modalService.send

  const { control } = formMethods
  const formState = useFormState({ control })

  const pageAnalyticsName = isEditing ? `${pageNameForAnalytics}Edit` : pageNameForAnalytics
  useSendPageViewEvent(analytics, pageAnalyticsName)

  const isFormDirty = isDirtyFromUserInteraction(formState)

  const continueAction = intl.formatMessage({ id: 'common-continue-anyway' })
  const editHeader = intl.formatMessage({ id: 'common-re-edit-prompt-header' })
  const editMessage = intl.formatMessage({ id: 'common-re-edit-prompt-message' }, { continue_action: continueAction })

  const introProps: IntroSectionProps = { ...pageIntro }
  let progress: ProgressBarStepProps | undefined = undefined

  if (progressBarOptions) {
    const progressText = getProgressText(intl, progressBarOptions)

    if (!introProps.progressText) {
      introProps.progressText = progressText
    }

    progress = {
      ...progressBarOptions,
      'aria-label': progressText,
    }
  }

  const onBackWithDirtyCheck = () => {
    if (!onBack) {
      return
    }

    if (isFormDirty) {
      analytics.sendModalViewEvent('DiscardChangesModal')
      modalSend(
        getCancelEditingPromptModalEvent({
          onConfirm: () => {
            analytics.sendCustomEvent('DiscardChangesModal', 'ConfirmButton')
            onBack()
          },
          onCancel: () => {
            analytics.sendCustomEvent('DiscardChangesModal', 'CancelButton')
          },
          intl,
          header: editHeader,
          text: editMessage,
        }),
      )
    } else {
      onBack()
    }
  }

  const onSubmitWithConfirmCheck = () => {
    if (isFormDirty && isEditing && hasDataDependencies) {
      const analyticsCategory = 'ConfirmDataDependencyUpdateModal'
      analytics.sendModalViewEvent(analyticsCategory)
      modalSend(
        getConfirmDependantApplicationDataChangeModal({
          onConfirm: () => {
            analytics.sendCustomEvent(analyticsCategory, 'ConfirmButton')
            onSubmit()
          },
          onCancel: () => {
            analytics.sendCustomEvent(analyticsCategory, 'CancelButton')
          },
          intl,
        }),
      )
    } else {
      onSubmit()
    }
  }

  const onConfirm = () => {
    analytics.sendCustomEvent(pageAnalyticsName, 'ContinueButton')
    onSubmitWithConfirmCheck()
  }

  usePageTitle(pageIntro.title)

  const { subHeaderOptions, headerButtonProps, confirmButtonProps }: WizardStepConfig = createStepConfigurations({
    stepTitle: pageIntro.title,
    intl,
    isEditing: !!isEditing,
    buttonHandlers: {
      handleContinueClick: onConfirm,
      handleBackClick: onBack
        ? customBackAction
          ? () => customBackAction(onBackWithDirtyCheck)
          : onBackWithDirtyCheck
        : undefined,
      handleInfoClick: onInfo,
      handleAbortClick: onExit,
    },
    continueLabel: submitLabelOverride,
    progressInfo: progress
      ? {
          stepIndex: progress?.currentStepIndex,
          totalSteps: progress?.totalSteps,
        }
      : undefined,
    workInProgress: isLoading ? { busyMode: BusyMode.Loading } : undefined,
    leadText: pageIntro.leadText,
    additionalText: pageIntro.additionalText,
    showIntroSectionDivider: pageIntro.showIntroSectionDivider,
  })

  return (
    <>
      {onBackToSummary && (
        <BackToSummaryTopBar isEditing={!!isEditing} isFormDirty={isFormDirty} onBackConfirmed={onBackToSummary} />
      )}
      <WizardStep
        stepId={stepId}
        isLoading={!!isLoading}
        title={title}
        icon={LogoIconType.SPankki}
        shouldShowMainHeader={shouldShowMainHeader && !onBackToSummary}
        subHeaderOptions={subHeaderOptions}
        confirmButtonProps={confirmButtonProps}
        customFooter={customFooter}
        headerButtonProps={headerButtonProps}
        transparentTopBar={true}
        pageIntro={pageIntro}
        showExitConfirmation={showExitConfirmation}
      >
        <FormSectionContainer>
          <FormProvider {...formMethods}>
            <ImplicitlySubmittableForm onSubmit={onSubmit} isLoading={isLoading}>
              <FormSectionContainer>{children}</FormSectionContainer>
            </ImplicitlySubmittableForm>
          </FormProvider>
        </FormSectionContainer>
      </WizardStep>
    </>
  )
}
